import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    getGaClientId,
    handleCloseInputDropdownOnBlur,
    handleLoadSharpsringScriptUrl,
    handleSetSharpspringScript,
    handleSharpspringSubmit,
    setCaptchaEuScript,
    normalizeModelName,
} from '../utils';
import { isCaptchaEnabled } from '../services/captcha';
import { getFormRenderToken } from '../services/anti-bot';

Vue.component('promo-form-component', {
    props: {
        siteHandle: {
            value: String,
        },
        stores: {
            value: Object,
        },
        sharpspringBaseUrl: {
            value: String,
        },
        sharpspringEndpointId: {
            value: String,
        },
        sharpspringScriptUrl: {
            value: String,
        },
        captchaEuPublicKey: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            requestType: 'private',
            requestIndex: 0,
            countryHandle: '',
            selected: '',

            dropdownCountry: null,
            dropdownStore: null,
            dropdownHeardFrom: null,
            heardFrom: null,
            financingType: '',
            financingTypeValue: '',

            isNumberValid: true,

            modelDropdownUpdateCountries: [
                'mgMotorsFrance',
                'mgMotorsSwitzerlandDe',
                'mgMotorsSwitzerlandIt',
                'mgMotorsSwitzerlandFr',
            ],

            newsletterSignUp: false,
        };
    },

    computed: {
        moreThenTwoStores: function () {
            if (!this.countryHandle) {
                return false;
            }

            return Object.keys(this.stores).length > 2;
        },
        hasBrandStores: function () {
            if (!this.countryHandle) {
                return false;
            }

            return Object.keys(this.stores).length > 0;
        },
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },
        toggleLoadingButtonClass() {
            return this.submitting ? 'button--loading' : '';
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }
        handleSetSharpspringScript(
            this.siteHandle,
            this.sharpspringBaseUrl,
            this.sharpspringEndpointId,
            'promoForm',
        );
        handleLoadSharpsringScriptUrl(
            this.siteHandle,
            this.sharpspringScriptUrl,
        );
        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });

        handleCloseInputDropdownOnBlur(
            '.new-input__dropdown-container',
            '#store',
        );

        EventBus.$on('promo-article-finance-plan-set', (selectedPlan) => {
            this.financingType = selectedPlan.text;
            this.financingTypeValue = selectedPlan.value;

            // Only for France and Switzerland: Update model dropdown when financing type changes since the financing type is tied to the model (model slugs are used)
            if (this.modelDropdownUpdateCountries.includes(this.siteHandle)) {
                EventBus.$emit('update-model-dropdown', selectedPlan.value);
            }
        });
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        showDropdown(key) {
            if (!this.hasBrandStores) {
                return false;
            }

            if (!this.countryHandle) {
                return false;
            }

            if (this.countryHandle == key) {
                return true;
            }
        },

        handleMarketingConsentChange(e) {
            e.preventDefault();
            const isConsentChecked = e.target.checked;
            this.newsletterSignUp = isConsentChecked;
            const hiddenMarketingInput = document.querySelector('#marketing');
            if (!hiddenMarketingInput) return;
            hiddenMarketingInput.setAttribute(
                'value',
                isConsentChecked ? '1' : '0',
            );
        },

        showRadioButtons(key) {
            if (!this.hasBrandStores) {
                return false;
            }

            if (!this.countryHandle) {
                return false;
            }

            if (this.moreThenTwoStores) {
                return false;
            }

            if (this.countryHandle == key) {
                return true;
            }
        },
        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let dataFromForm = new FormData(form);
            const data = {};

            for (let [key, val] of dataFromForm.entries()) {
                Object.assign(data, { [key]: val });
            }

            const dataLayerData = {
                event: 'lead',
                lead_type: 'Promo',
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: data.phone,
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            };

            if (this.countryHandle === 'mgMotorsPoland') {
                const uniqueFormId = crypto.randomUUID();

                dataLayerData.car_name = normalizeModelName(data.model);
                dataLayerData.unique_form_id = uniqueFormId;
            }

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(form, dataLayerData);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(
                    form,
                    dataLayerData,
                    captchaSolution,
                );
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, dataLayerData, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('gaClientId', getGaClientId());
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                formData.append('formRenderToken', this.formRenderToken);
                const response = await axios.post('/', formData);
                const result = response.data;

                if (result.data.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);

                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                this.sendGTMData(dataLayerData);

                handleSharpspringSubmit(
                    this.siteHandle,
                    this.sharpspringEndpointId,
                );

                // Success
                this.formCompleted = true;
                this.errors.clear();

                window.location = result.data.redirectUrl;
                this.scrollToTop();
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(dataToPush) {
            if (!dataToPush) return;
            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                window.dataLayer.push(dataToPush);
            }
        },

        toggleDropdown(event) {
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        setDropdownCountry(event) {
            this.dropdownCountry = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.countryHandle = value;
        },

        setDropdownSubject(event) {
            this.dropdownSubject = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selected = value;
        },

        setDropdownStore(event) {
            this.dropdownStore = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selectedBrandStore = value;
        },

        /**
         * Update heardFrom value when a selection takes place in its custom dropdown element.
         *
         * @param {ChangeEvent} event
         */
        setDropdownHeardFrom(event) {
            // Set text property to show value in template, set value in heardFrom property to send along with the form.
            this.dropdownHeardFrom = event.currentTarget.dataset.text;
            this.heardFrom = event.currentTarget.dataset.value;

            // Close the dropdown.
            this.toggleDropdown(event);
        },

        setDropdownFinancingType(event) {
            const financingTypeValue = event.currentTarget.dataset.value;
            this.financingType = event.currentTarget.dataset.text;
            this.financingTypeValue = financingTypeValue;
            this.toggleDropdown(event);

            EventBus.$emit('promo-financing-type-set', financingTypeValue);
        },
    },
});
